<script lang="ts">
  import Info from './Info.svelte';
  import { fade } from 'svelte/transition';
  import { Tween, type TweenedOptions } from 'svelte/motion';
  import { quintInOut } from 'svelte/easing';
  import type { AcademicState, TrainingState, WebState } from '../imports/builders';

  const tweenOptions: TweenedOptions<number> = {
    duration: 1000,
    easing: quintInOut,
  };

  interface Props {
    dashboardState: AcademicState|TrainingState|WebState
    val: number
    lastDate: string|null
    segment: string
  }

  let {
    dashboardState,
    val,
    lastDate = null,
    segment,
  }: Props = $props();

  let displayDate = $derived(dashboardState?.refreshDate || lastDate);
  $inspect(displayDate);
  let displayVal = $derived(dashboardState.gauge ?? (val || 0));
  let offset = $derived.by(()=> {
    if (displayVal >= 0 && !isNaN(displayVal)) {
      return ((1000 - (displayVal / 100) * 1000) * -1);
    } else {
      return 0;
    }
  });

  const tweenedVal = new Tween(0, tweenOptions);
  const tweenedOffset = new Tween(-1000, {
    delay: 1000,
    ...tweenOptions,
  });

  $effect(()=> {
    tweenedVal.set(Number(displayVal))
  });
  
  $effect(()=> {
    tweenedOffset.set(Number(offset))
  });
  
  let infoTitles = {
    'home': 'Welcome to UC\'s Digital Accessibility Gateway',
    'academic': 'Academic Dashboard',  
    'training': 'Training Dashboard',
    'web': 'Websites Dashboard',
    '3p': 'Third-party Tools Dashboard',
  };
  
  const infoTexts = {
    'home' : 'The University of Cincinnati is committed to providing an electronic experience that is accessible and supports success for every student, especially students with disabilities. This dashboard provides each college and unit with quantitative insight into the ongoing work related to building an inclusive and accessible culture and environment. With this information you will be able to compare progress over time, see where your college or business units falls compared to others and build measurable accessibility goals into your roadmap.' ,
    'academic' : 'Every UC student deserves to have access to course materials needed to successfully engage in learning. Ally for Canvas is a tool that provides automated accessibility testing for all course materials uploaded to Canvas. Utilizing the scores from Ally, this module provides insight into the current state of your college’s academic course content.' ,
    'training' : 'Employees at UC who meet the EIT Accessibility Training criteria are required to complete it biennially. This module lists faculty, staff and student workers in your college or unit along with their completion or exemption status for the required training.',
    'web' : 'UC websites are tested on a routine schedule using automated and manual testing methods. Use the automated testing scores from SiteImprove in this module to see how accessible your website is, the progress you have made and where you may need to make improvements.',
    '3p' : 'Software procured by UC undergoes an accessibility review in accordance with the EIT Accessibility Policy and the Accessibility Procurement Policy. This module contains information about products in use in your department that have gone through the accessibility review process.',
  };

  let openModal = () => {
    if(swal) {
      swal({
        title: infoTitles[segment],
        text: infoTexts[segment],
        confirmButtonColor: '#000',
        button: 'Got it, Thanks!',
      })
    } else {
      alert(infoTexts[segment])
    }
  }
</script>

<svelte:head>
  <script src='https://cdnjs.cloudflare.com/ajax/libs/sweetalert/2.1.2/sweetalert.min.js' integrity='sha512-AA1Bzp5Q0K1KanKKmvN/4d3IRKVlv9PYgwFPvm32nPO6QS8yH1HO7LbgB1pgiOxPtfeg5zEn2ba64MUcqJx6CA==' crossorigin='anonymous' referrerpolicy='no-referrer'></script>
  <style lang="scss">
    button.swal-button.swal-button--confirm {
    background-color: #e00122;
    color: #fff;
    &:hover, &:focus {
      background-color: #000;
    }
}
  </style>
</svelte:head>

<div class='gauge' aria-live='polite'>
  {#if segment}
    <div class='info-wrap' in:fade>
      <Info onClickFunction={openModal} />
    </div>
  {/if}
  <div class='graphics-wrap'>
    <div class='gauge-bg'>
      <svg
        role='presentation'
        viewBox='0 0 500 500'
        preserveAspectRatio='xMinYMin meet'
        class='bg-circle'
      >
        <g>
          <circle r='250' cx='50%' cy='50%' class='circle-back' />
        </g>
      </svg>
    </div>
    <div class='gauge-output'>
      <h3><span class='value'>{tweenedVal.current.toFixed(1)}<em>%</em></span></h3>
    </div>
    <svg
      aria-label={segment === 'academic' ? `${tweenedVal.current.toFixed(1)}% course accessibility` : segment === 'training' ? `${tweenedVal.current.toFixed(1)}% trained, updated daily` : segment === 'web' ? `${tweenedVal.current.toFixed(1)}% accessibility score, updated daily` : `${tweenedVal.current.toFixed(1)}% accessibility score`}
      version='1.1'
      xmlns='http://www.w3.org/2000/svg'
      xmlns:xlink='http://www.w3.org/1999/xlink'
      x='0px'
      y='0px'
      viewBox='0 0 415 394'
      style='enable-background:new 0 0 415 394;'
      xml:space='preserve'
    >
      <g>
        <g>
          <g>
            <g>
              <g>
                <g>
                  <linearGradient
                    id='SVGID_1_'
                    gradientUnits='userSpaceOnUse'
                    x1='218.8953'
                    y1='1003.7877'
                    x2='218.8953'
                    y2='656.5329'
                    gradientTransform='matrix(0.999 4.361898e-02 -4.361898e-02 0.999 22.301 -659.7161)'
                  >
                    <stop offset='0' style='stop-color:#1A1A1A' />
                    <stop offset='1' style='stop-color:#666666' />
                  </linearGradient>
                  <path
                    class='st0'
                    d='M331.8,330.4c32.5-31.6,51.8-74.5,53.8-119.9c4.4-99.3-73.4-183.4-173.8-187.8S26.3,95.1,21.9,194.4
                    c-2.3,50.9,17.4,100.4,53.9,135.9'
                  />
                  <linearGradient
                    id='SVGID_2_'
                    gradientUnits='userSpaceOnUse'
                    x1='36.8592'
                    y1='835.4866'
                    x2='400.8592'
                    y2='835.4866'
                    gradientTransform='matrix(0.999 4.361898e-02 -4.318279e-02 0.9891 22.3191 -656.7592)'
                  >
                    <stop offset='0.14' style='stop-color:#C0272D' />
                    <stop offset='0.35' style='stop-color:#BE2A2E' />
                    <stop offset='0.48' style='stop-color:#B5322F' />
                    <stop offset='0.59' style='stop-color:#A84032' />
                    <stop offset='0.68' style='stop-color:#945536' />
                    <stop offset='0.77' style='stop-color:#7B6F3C' />
                    <stop offset='0.85' style='stop-color:#5C9043' />
                    <stop offset='0.92' style='stop-color:#39B44A' />
                  </linearGradient>
                  <path
                    class='outer-ring'
                    d='M331.8,330.4c32.5-31.6,51.8-74.5,53.8-119.9c4.4-99.3-73.4-183.4-173.8-187.8S26.3,95.1,21.9,194.4
                    c-2.3,50.9,17.4,100.4,53.9,135.9'
                    stroke='#000'
                    stroke-dasharray='1001 1001'
                    stroke-dashoffset={tweenedOffset.current}
                    pathLength='1000'
                  />
                  <radialGradient
                    id='SVGID_3_'
                    cx='203.6'
                    cy='880.4278'
                    r='197.8917'
                    gradientTransform='matrix(1 0 0 1.01 0 -685.36)'
                    gradientUnits='userSpaceOnUse'
                  >
                    <stop offset='0' style='stop-color:#1A1A1A' />
                    <stop offset='1' style='stop-color:#333333' />
                  </radialGradient>
                </g>
              </g>
            </g>
          </g>
        </g>
      </g>
    </svg>
  </div>
  <div class='percent-values'>
    <div class='row gauge-decorations'>
      <div class='col dark-label'>0%</div>
      <div class='col'></div>
      <div class='col dark-label'>100%</div>
    </div>
  </div>
  {#if displayDate}
    <div class='gauge-date'>
      <p><span>Updated</span><time datetime={ displayDate }>{ displayDate }</time></p>
    </div>
  {/if}
</div>

<style type='text/scss'>
  .info-wrap {
    text-align: right;
  }

  .gauge {
    position: relative;
    color: #fff;
    text-align: center;
    width: 100%;
    max-width: 410px;
    margin: 0 auto;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
      'Helvetica Neue', Arial, 'Noto Sans', 'Liberation Sans', sans-serif,
      'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
      'Noto Color Emoji';
  }

  .gauge .graphics-wrap {
    height: 0;
    padding-top: 90%;
    position: relative;
    /*background: radial-gradient(100px circle at 100px 100px, #333 100%, transparent 100%);*/
  }

  .gauge .graphics-wrap > svg {
    position: absolute;
    top: 0;
    left: 0;
    /*top: -27px;*/
    width: 100%;
    /*left: -24px;*/
  }

  .gauge .gauge-bg {
    width: 100%;
    padding: 5% 6% 0% 5%;
    position: absolute;
    top: 0;
    left: 0;
  }

  .gauge .gauge-bg > svg.bg-circle {
    position: relative;
    display: block;
  }

  .gauge .gauge-output {
    margin: 0 auto;
    text-align: center;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .gauge .gauge-output h3 {
    font-size: 3.5rem;
    line-height: 5rem;
    color: inherit;
    text-transform: uppercase;
    font-weight: 100;
  }

  @media (max-width: 1024px) and (min-width: 768px) {
    .gauge .gauge-output h3 {
      font-size: 2.8vw;
      line-height: 0.5;
    }
  }
  @media (max-width: 767px) {
    .gauge .gauge-output h3 {
      font-size: 3vw;
      line-height: 0.8;
    }
  }
  @media (max-width: 575px) {
    .gauge .gauge-output h3 {
      font-size: 7vw;
      line-height: 0.8;
    }
  }
  .gauge .gauge-output span.value {
    display: block;
    font-size: 3em;
    padding: 20px 0 20px 0;
  }
  .gauge .gauge-output span.value em {
    font-style: unset;
    font-size: 0.65em;
  }

  .gauge .percent-values {
    display: block;
    position: relative;
    padding: 1.5rem;
  }
  .gauge .gauge-decorations {
    position: absolute;
    width: 100%;
    bottom: 8%;
  }

  .gauge .gauge-date {
    line-height: 1.2;
    font-size: 2rem;
    padding: 0 0 20px;
  }
  .gauge .gauge-date p {
    margin: 0;
  }
  .gauge .gauge-date time {
    color: #3f3f3f;
    text-transform: uppercase;
    font-weight: 400;
    display: block;
  }
  .gauge .gauge-date span {
    color: #212529;
    display: block;
    font-weight: 700;
    text-transform: uppercase;
  }

  .row {
    display: flex;
    flex-wrap: wrap;
  }

  .col {
    flex: 0 0 33.3333333333%;
    max-width: 33.3333333333%;
    position: relative;
    width: 100%;
  }

  .dark-label {
    color: #333;
  }

  /* SVG inline styles*/
  .st0 {
    opacity: 0.35;
    fill: none;
    stroke: url(#SVGID_1_);
    stroke-width: 34;
    stroke-linecap: round;
    stroke-linejoin: round;
    enable-background: new;
  }
  .outer-ring {
    fill: none;
    stroke: url(#SVGID_2_);
    stroke-width: 20;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
</style>
